/**
 * Documentation Search Service - Client-side search using Fuse.js
 *
 * This service handles:
 * - Initializing the search index from search-index.json
 * - Searching documents using Fuse.js fuzzy search
 * - Tracking initialization state
 *
 * Context: AC-658 - Create docsService and docsSearchService
 *
 * Technical Design:
 * - Uses Fuse.js for client-side fuzzy search
 * - Loads search-index.json containing pre-extracted content
 * - Returns DocSearchResult[] with item, score, and matches
 */

import Fuse, { IFuseOptions, FuseResult } from 'fuse.js';
import { DocSearchResult, DocItem } from '../types/help';

/** Base URL for documentation files */
const DOCS_BASE_URL = '/docs';

/**
 * Search index entry structure (from search-index.json)
 */
interface SearchIndexEntry {
  id: string;
  title: string;
  path: string;
  description?: string;
  category: string;
  content: string;
}

/**
 * Search index JSON structure
 */
interface SearchIndex {
  entries: SearchIndexEntry[];
  lastUpdated: string;
}

/**
 * Documentation search service interface
 */
interface DocsSearchService {
  /**
   * Initialize the search index from search-index.json
   */
  initialize(): Promise<void>;

  /**
   * Search documents by query string
   */
  search(query: string): DocSearchResult[];

  /**
   * Check if search index is loaded
   */
  isInitialized(): boolean;

  /**
   * Reset the service state (for testing)
   */
  reset(): void;
}

// Service state
let initialized = false;
let fuseInstance: Fuse<SearchIndexEntry> | null = null;

/**
 * Fuse.js configuration options
 * - Search in title, description, and content fields
 * - Use fuzzy matching with reasonable threshold
 * - Include score for ranking results
 */
const fuseOptions: IFuseOptions<SearchIndexEntry> = {
  keys: [
    { name: 'title', weight: 2 },
    { name: 'description', weight: 1.5 },
    { name: 'content', weight: 1 }
  ],
  includeScore: true,
  threshold: 0.4,
  ignoreLocation: true,
  minMatchCharLength: 2
};

const docsSearchService: DocsSearchService = {
  /**
   * Initialize the search index from /docs/search-index.json
   * Sets up Fuse.js instance for searching
   * @throws Error if fetch fails or response is not ok
   */
  initialize: async (): Promise<void> => {
    const response = await fetch(`${DOCS_BASE_URL}/search-index.json`);

    if (!response.ok) {
      throw new Error(`Failed to load search index: ${response.status} ${response.statusText}`);
    }

    const searchIndex: SearchIndex = await response.json();

    // Create Fuse.js instance with the entries
    fuseInstance = new Fuse(searchIndex.entries, fuseOptions);
    initialized = true;
  },

  /**
   * Search documents by query string using Fuse.js fuzzy search
   * @param query - The search query string
   * @returns Array of DocSearchResult objects sorted by relevance
   */
  search: (query: string): DocSearchResult[] => {
    // Return empty array if not initialized
    if (!initialized || !fuseInstance) {
      return [];
    }

    // Return empty array for empty or whitespace-only queries
    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      return [];
    }

    // Perform search with Fuse.js
    const fuseResults: FuseResult<SearchIndexEntry>[] = fuseInstance.search(trimmedQuery);

    // Transform Fuse.js results to DocSearchResult format
    return fuseResults.map((result): DocSearchResult => {
      const entry = result.item;

      // Convert SearchIndexEntry to DocItem
      const docItem: DocItem = {
        id: entry.id,
        title: entry.title,
        path: entry.path,
        description: entry.description
      };

      return {
        item: docItem,
        score: result.score ?? 0
      };
    });
  },

  /**
   * Check if the search index has been loaded
   * @returns true if initialized, false otherwise
   */
  isInitialized: (): boolean => {
    return initialized;
  },

  /**
   * Reset the service state (primarily for testing purposes)
   */
  reset: (): void => {
    initialized = false;
    fuseInstance = null;
  }
};

export default docsSearchService;
